import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { NotificationPreferences, ApiKey, ApiKeyCreateResponse } from '../types';
import '../styles/account-preferences.css';

type MarketplaceChannelKey = 'created' | 'participated' | 'watched';
type ForumChannelKey = 'created' | 'participated' | 'watched';

const DEFAULT_PREFERENCES: NotificationPreferences = {
  marketplace: {
    created: { email: true },
    participated: { email: true },
    watched: { email: true },
    pendingApprovals: true
  },
  forum: {
    created: { email: true },
    participated: { email: true },
    watched: { email: true }
  }
};

function clonePreferences(preferences: NotificationPreferences | null | undefined) {
  const source = preferences ?? DEFAULT_PREFERENCES;
  return {
    marketplace: {
      created: { email: source.marketplace.created.email },
      participated: { email: source.marketplace.participated.email },
      watched: { email: source.marketplace.watched.email },
      pendingApprovals: source.marketplace.pendingApprovals
    },
    forum: {
      created: { email: source.forum.created.email },
      participated: { email: source.forum.participated.email },
      watched: { email: source.forum.watched.email }
    }
  };
}

function hasPreferenceChanges(
  a: NotificationPreferences | null,
  b: NotificationPreferences | null
) {
  if (!a || !b) {
    return Boolean(a) !== Boolean(b);
  }
  return (
    a.marketplace.created.email !== b.marketplace.created.email ||
    a.marketplace.participated.email !== b.marketplace.participated.email ||
    a.marketplace.watched.email !== b.marketplace.watched.email ||
    a.marketplace.pendingApprovals !== b.marketplace.pendingApprovals ||
    a.forum.created.email !== b.forum.created.email ||
    a.forum.participated.email !== b.forum.participated.email ||
    a.forum.watched.email !== b.forum.watched.email
  );
}

export function AccountPreferencesPage() {
  const { user, refreshUser, logout } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(
    user ? clonePreferences(user.notificationPreferences) : null
  );
  const [savedPreferences, setSavedPreferences] = useState<NotificationPreferences | null>(
    user ? clonePreferences(user.notificationPreferences) : null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(true);
  const [apiKeysError, setApiKeysError] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.accountPreferences();
        if (!active) {
          return;
        }
        const next = clonePreferences(response.notificationPreferences);
        setPreferences(next);
        setSavedPreferences(next);
      } catch (err) {
        if (active) {
          setError((err as Error).message || 'Unable to load notification preferences.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadKeys = async () => {
      setApiKeysLoading(true);
      setApiKeysError(null);
      try {
        const response = await api.listApiKeys();
        if (active) setApiKeys(response.keys);
      } catch (err) {
        if (active) setApiKeysError((err as Error).message || 'Unable to load API keys.');
      } finally {
        if (active) setApiKeysLoading(false);
      }
    };
    void loadKeys();
    return () => { active = false; };
  }, []);

  const handleCreateKey = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    if (!newKeyName.trim()) return;
    setCreatingKey(true);
    setApiKeysError(null);
    try {
      const response = await api.createApiKey(newKeyName.trim());
      setCreatedKey(response);
      setNewKeyName('');
      const listResponse = await api.listApiKeys();
      setApiKeys(listResponse.keys);
    } catch (err) {
      setApiKeysError((err as Error).message || 'Unable to create API key.');
    } finally {
      setCreatingKey(false);
    }
  }, [newKeyName]);

  const handleRevokeKey = useCallback(async (id: string) => {
    setRevokingKeyId(id);
    setApiKeysError(null);
    try {
      await api.revokeApiKey(id);
      setApiKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (err) {
      setApiKeysError((err as Error).message || 'Unable to revoke API key.');
    } finally {
      setRevokingKeyId(null);
    }
  }, []);

  const handleCopyKey = useCallback(async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }, []);

  const handleMarketplaceToggle = useCallback(
    (key: MarketplaceChannelKey) => (event: ChangeEvent<HTMLInputElement>) => {
      setSuccess(null);
      const checked = event.target.checked;
      setPreferences((current) => {
        if (!current) {
          return current;
        }
        const next = clonePreferences(current);
        next.marketplace = {
          ...next.marketplace,
          [key]: { email: checked }
        } as NotificationPreferences['marketplace'];
        return next;
      });
    },
    []
  );

  const handleForumToggle = useCallback(
    (key: ForumChannelKey) => (event: ChangeEvent<HTMLInputElement>) => {
      setSuccess(null);
      const checked = event.target.checked;
      setPreferences((current) => {
        if (!current) {
          return current;
        }
        const next = clonePreferences(current);
        next.forum = {
          ...next.forum,
          [key]: { email: checked }
        } as NotificationPreferences['forum'];
        return next;
      });
    },
    []
  );

  const handleMarketplacePendingToggle = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSuccess(null);
    setPreferences((current) => {
      if (!current) {
        return current;
      }
      const next = clonePreferences(current);
      next.marketplace.pendingApprovals = checked;
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!preferences) {
        return;
      }
      setSaving(true);
      setError(null);
      setSuccess(null);
      try {
        const response = await api.updateAccountPreferences({ notificationPreferences: preferences });
        const next = clonePreferences(response.notificationPreferences);
        setPreferences(next);
        setSavedPreferences(next);
        setSuccess('Preferences saved.');
        await refreshUser();
      } catch (err) {
        setError((err as Error).message || 'Unable to save preferences.');
      } finally {
        setSaving(false);
      }
    },
    [preferences, refreshUser]
  );

  const changesPending = useMemo(
    () => hasPreferenceChanges(preferences, savedPreferences),
    [preferences, savedPreferences]
  );
  const deleteConfirmationWord = 'DELETE';

  const marketplaceCreatedChecked = preferences?.marketplace.created.email ?? true;
  const marketplaceParticipatedChecked = preferences?.marketplace.participated.email ?? true;
  const marketplaceWatchedChecked = preferences?.marketplace.watched.email ?? true;
  const marketplacePendingChecked = preferences?.marketplace.pendingApprovals ?? true;
  const forumCreatedChecked = preferences?.forum.created.email ?? true;
  const forumParticipatedChecked = preferences?.forum.participated.email ?? true;
  const forumWatchedChecked = preferences?.forum.watched.email ?? true;
  const isAdmin = user?.role === 'admin';

  const handleDeleteAccount = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setDeleteError(null);
      if (deleteConfirm.trim().toUpperCase() !== deleteConfirmationWord) {
        setDeleteError(`Type ${deleteConfirmationWord} to confirm deletion.`);
        return;
      }
      setDeleteBusy(true);
      try {
        await api.deleteAccount();
        await logout();
      } catch (err) {
        setDeleteError((err as Error).message || 'Unable to delete account right now.');
      } finally {
        setDeleteBusy(false);
      }
    },
    [deleteConfirm, deleteConfirmationWord, logout]
  );

  return (
    <div className="account-preferences">
      <header className="account-preferences__header">
        <h1>Account Settings</h1>
        <p>Manage how we contact you and control your personal data.</p>
      </header>

      {loading && <div className="account-preferences__status">Loading…</div>}
      {!loading && error && <div className="account-preferences__error">{error}</div>}
      {!loading && success && <div className="account-preferences__success">{success}</div>}

      {!loading && preferences && (
        <section className="account-preferences__card">
          <h2 className="account-preferences__card-title">Notification preferences</h2>
          <p className="account-preferences__card-intro">
            Choose which email notifications you want to receive from the marketplace and forum.
          </p>
          <form onSubmit={handleSubmit}>
          <fieldset>
            <legend>Marketplace</legend>
            <p className="account-preferences__description">
              Control emails about marketplace submissions you create, participate in, or watch.
            </p>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={marketplaceCreatedChecked}
                onChange={handleMarketplaceToggle('created')}
              />
              <div>
                <strong>Entries I create</strong>
                <p>Receive notifications about reviews and comments on entries you submit.</p>
              </div>
            </label>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={marketplaceParticipatedChecked}
                onChange={handleMarketplaceToggle('participated')}
              />
              <div>
                <strong>Entries I participate in</strong>
                <p>Receive follow-up notifications on entries where you join the discussion.</p>
              </div>
            </label>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={marketplaceWatchedChecked}
                onChange={handleMarketplaceToggle('watched')}
              />
              <div>
                <strong>Entries I watch</strong>
                <p>Receive notifications for entries you explicitly watch from the detail page.</p>
              </div>
            </label>
            {isAdmin && (
              <label className="preference-toggle">
                <input
                  type="checkbox"
                  checked={marketplacePendingChecked}
                  onChange={handleMarketplacePendingToggle}
                />
                <div>
                  <strong>Pending entry alerts</strong>
                  <p>Get an email when a new entry needs admin approval.</p>
                </div>
              </label>
            )}
          </fieldset>

          <fieldset>
            <legend>Forum</legend>
            <p className="account-preferences__description">
              Manage email updates for forum threads you create, participate in, or watch.
            </p>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={forumCreatedChecked}
                onChange={handleForumToggle('created')}
              />
              <div>
                <strong>Threads I start</strong>
                <p>Receive notifications when others reply or mark answers on threads you open.</p>
              </div>
            </label>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={forumParticipatedChecked}
                onChange={handleForumToggle('participated')}
              />
              <div>
                <strong>Threads I participate in</strong>
                <p>Stay updated on replies and changes in threads where you comment.</p>
              </div>
            </label>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={forumWatchedChecked}
                onChange={handleForumToggle('watched')}
              />
              <div>
                <strong>Threads I watch</strong>
                <p>Receive updates for threads you've explicitly chosen to watch.</p>
              </div>
            </label>
          </fieldset>

          <div className="account-preferences__actions">
            <button type="submit" disabled={saving || !changesPending}>
              {saving ? 'Saving…' : 'Save preferences'}
            </button>
          </div>
          </form>
        </section>
      )}

      {!loading && (
        <section className="account-preferences__card">
          <h2 className="account-preferences__card-title">API keys</h2>
          <p className="account-preferences__card-intro">
            Generate API keys for programmatic access. Keys are shown only once at creation
            time &mdash; store them securely.
          </p>

          {apiKeysError && (
            <div className="account-preferences__error">{apiKeysError}</div>
          )}

          {createdKey && (
            <div className="api-keys__created-banner">
              <p className="api-keys__created-label">
                Your new API key for <strong>{createdKey.name}</strong>:
              </p>
              <div className="api-keys__created-value">
                <code className="api-keys__key-code">{createdKey.key}</code>
                <button
                  className="api-keys__copy-button"
                  onClick={() => handleCopyKey(createdKey.key)}
                >
                  {keyCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="api-keys__created-warning">
                This key will not be shown again. Copy it now and store it securely.
              </p>
              <button
                className="api-keys__dismiss-button"
                onClick={() => setCreatedKey(null)}
              >
                I have saved my key
              </button>
            </div>
          )}

          <form className="api-keys__create-form" onSubmit={handleCreateKey}>
            <label htmlFor="api-key-name">Key name</label>
            <div className="api-keys__create-row">
              <input
                id="api-key-name"
                type="text"
                placeholder="e.g. Chrome Extension"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                maxLength={100}
                disabled={creatingKey}
              />
              <button type="submit" disabled={creatingKey || !newKeyName.trim()}>
                {creatingKey ? 'Creating\u2026' : 'Generate key'}
              </button>
            </div>
          </form>

          {apiKeysLoading && (
            <div className="account-preferences__status">Loading keys\u2026</div>
          )}

          {!apiKeysLoading && apiKeys.length === 0 && (
            <p className="api-keys__empty">No API keys yet.</p>
          )}

          {!apiKeysLoading && apiKeys.length > 0 && (
            <div className="api-keys__list">
              {apiKeys.map((apiKey) => (
                <div key={apiKey.id} className="api-keys__item">
                  <div className="api-keys__item-info">
                    <span className="api-keys__item-name">{apiKey.name}</span>
                    <span className="api-keys__item-prefix">
                      <code>{apiKey.keyPrefix}\u2026</code>
                    </span>
                    <span className="api-keys__item-meta">
                      Created {new Date(apiKey.createdAt).toLocaleDateString()}
                      {apiKey.isExpired
                        ? ' \u00b7 Expired'
                        : ` \u00b7 Expires ${new Date(apiKey.expiresAt).toLocaleDateString()}`}
                      {apiKey.lastUsedAt &&
                        ` \u00b7 Last used ${new Date(apiKey.lastUsedAt).toLocaleDateString()}`}
                    </span>
                  </div>
                  <button
                    className="api-keys__revoke-button"
                    onClick={() => handleRevokeKey(apiKey.id)}
                    disabled={revokingKeyId === apiKey.id}
                  >
                    {revokingKeyId === apiKey.id ? 'Revoking\u2026' : 'Revoke'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {!loading && (
        <section className="account-preferences__danger-card">
          <h2>Delete account</h2>
          <p>
            This permanently removes your profile details and immediately signs you out. Your marketplace entries,
            forum activity, and requests remain for historical accuracy but will show as coming from a “Deleted account”.
          </p>
          <form className="account-preferences__danger-form" onSubmit={handleDeleteAccount}>
            <label htmlFor="delete-confirm">
              Type <span className="account-preferences__danger-pill">{deleteConfirmationWord}</span> to
              confirm.
            </label>
            <input
              id="delete-confirm"
              type="text"
              autoComplete="off"
              value={deleteConfirm}
              onChange={(event) => {
                setDeleteConfirm(event.target.value);
                if (deleteError) {
                  setDeleteError(null);
                }
              }}
              placeholder={deleteConfirmationWord}
              disabled={deleteBusy}
            />
            {deleteError ? (
              <div className="account-preferences__danger-error">{deleteError}</div>
            ) : null}
            <div className="account-preferences__danger-actions">
              <button type="submit" disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete my account'}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
